import React, { useEffect } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const CLICKUP_FORM_URL =
  "https://forms.clickup.com/9011636302/f/8cj5a2e-61811/2Q0Y7VJYXM9GNIVUC0";
const CLICKUP_EMBED_SCRIPT_ID = "clickup-forms-embed-script";
const CLICKUP_EMBED_SCRIPT_SRC =
  "https://app-cdn.clickup.com/assets/js/forms-embed/v1.js";

const GUIDANCE_SECTIONS = [
  {
    title: "Give the Issue A Title",
    body: "A short name helps us quickly see what's happening, where. Be descriptive! \"Content Editor doesn't save bolded text\" helps our team lock in on the trouble."
  },
  {
    title: "Describe the Error and Steps to Reproduce",
    body: 'Give a brief description of the bug in 3 to 4 sentences, if possible. Use specific language and avoid using "it" of "this" to describe steps and tools. The more specific you are the better. Are you getting an error message? What browser are you using?'
  },
  {
    title: "A Picture (or Video) is Worth 1000 Words",
    body: "Upload a screenshot or screen recording. These are incredibly helpful."
  },
  {
    title: "What is the Impact?",
    body: "Who is affected, and how? While not a requirement, helping us understand the metrics, SLAs, or KPIs impacted helps us prioritize our responses. Does this effect .01% of our users (a corner case) or is this enterprise-wide?"
  },
  {
    title: "How this should Work",
    body: "By understanding the goal or expected outcome, we can better plan our responses."
  }
] as const;

interface ReportIssueDialogProps {
  open: boolean;
  onClose: () => void;
}

const ReportIssueDialog: React.FC<ReportIssueDialogProps> = ({
  open,
  onClose
}) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (!open || document.getElementById(CLICKUP_EMBED_SCRIPT_ID)) return;

    const script = document.createElement("script");
    script.id = CLICKUP_EMBED_SCRIPT_ID;
    script.src = CLICKUP_EMBED_SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);
  }, [open]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      aria-labelledby="report-issue-dialog-title"
    >
      <DialogTitle
        id="report-issue-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0
        }}
      >
        <Typography component="span" variant="h6" fontWeight={700}>
          Report Issue
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: isNarrow ? "column" : "row",
          flex: 1,
          minHeight: 0,
          p: 0,
          overflow: "hidden"
        }}
      >
        <Box
          component="aside"
          sx={{
            flex: isNarrow ? "0 0 auto" : "0 0 42%",
            maxWidth: isNarrow ? "none" : 520,
            overflow: "auto",
            p: { xs: 2, sm: 3 },
            borderRight: isNarrow ? 0 : 1,
            borderBottom: isNarrow ? 1 : 0,
            borderColor: "divider",
            bgcolor: "background.default"
          }}
        >
          <Typography variant="body1" color="text.primary" paragraph>
            A good bug report helps us identify issues quickly. Take a moment to
            articulate what happened, how you arrived at the bug, and what you
            expected to happen. Screenshots are always helpful as are screen
            recordings. You&apos;ll find a handy template below to help you get
            us what we need.
          </Typography>

          {GUIDANCE_SECTIONS.map(({ title, body }) => (
            <Box key={title} sx={{ mb: 2.5 }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                gutterBottom
                color="text.primary"
              >
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {body}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: isNarrow ? 360 : 0,
            overflow: "auto",
            p: { xs: 1.5, sm: 2 },
            display: "flex",
            flexDirection: "column"
          }}
        >
          <Box
            sx={{
              flex: 1,
              width: "100%",
              minHeight: isNarrow ? 320 : "100%",
              "& iframe": {
                display: "block",
                width: "100%",
                minHeight: isNarrow ? 320 : "calc(100vh - 80px)",
                borderRadius: 1
              }
            }}
          >
            <iframe
              className="clickup-embed clickup-dynamic-height"
              src={CLICKUP_FORM_URL}
              width="100%"
              height="100%"
              title="Report an issue"
              style={{
                background: "transparent",
                border: "1px solid #ccc"
              }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReportIssueDialog;
